import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faExclamationTriangle,
  faTrash,
  faQuestion,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons'
import { clsx } from 'clsx'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './Dialog'
import { Button } from './Button'

export type ConfirmVariant = 'danger' | 'warning' | 'info' | 'default'

export interface ConfirmOptions {
  title: string
  message: string
  variant?: ConfirmVariant
  confirmText?: string
  cancelText?: string
  confirmButtonVariant?: 'primary' | 'danger' | 'destructive' | 'secondary'
}

interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void
}

interface ConfirmContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>
  alert: (
    title: string,
    message?: string,
    variant?: ConfirmVariant
  ) => Promise<void>
}

const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider')
  return ctx
}

const variantConfig: Record<
  ConfirmVariant,
  { icon: typeof faExclamationTriangle; iconBg: string; iconColor: string }
> = {
  danger: {
    icon: faTrash,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
  },
  warning: {
    icon: faExclamationTriangle,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
  },
  info: {
    icon: faInfoCircle,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  default: {
    icon: faQuestion,
    iconBg: 'bg-neutral-100',
    iconColor: 'text-neutral-600',
  },
}

interface ConfirmProviderProps {
  children: ReactNode
}

export function ConfirmProvider({ children }: ConfirmProviderProps) {
  const [state, setState] = useState<ConfirmState | null>(null)

  const confirm = useCallback(
    (options: ConfirmOptions): Promise<boolean> =>
      new Promise((resolve) => {
        setState({ ...options, resolve })
      }),
    []
  )

  const alert = useCallback(
    (
      title: string,
      message = '',
      variant: ConfirmVariant = 'info'
    ): Promise<void> =>
      new Promise((resolve) => {
        setState({
          title,
          message,
          variant,
          confirmText: 'Aceptar',
          cancelText: '',
          resolve: () => resolve(),
        })
      }),
    []
  )

  const handleConfirm = useCallback(() => {
    state?.resolve(true)
    setState(null)
  }, [state])

  const handleCancel = useCallback(() => {
    state?.resolve(false)
    setState(null)
  }, [state])

  const config = state ? variantConfig[state.variant ?? 'default'] : null
  const isAlert = !!state && !state.cancelText
  const buttonVariant =
    state?.confirmButtonVariant ||
    (state?.variant === 'danger' ? 'destructive' : 'primary')

  return (
    <ConfirmContext.Provider value={{ confirm, alert }}>
      {children}
      <Dialog
        open={!!state}
        onOpenChange={(open) => {
          if (!open) handleCancel()
        }}
      >
        {state && config && (
          <DialogContent size="md" showClose={isAlert}>
            <DialogHeader>
              <div className="flex items-start gap-3">
                <div
                  className={clsx(
                    'shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
                    config.iconBg
                  )}
                >
                  <FontAwesomeIcon
                    icon={config.icon}
                    className={clsx('text-base', config.iconColor)}
                  />
                </div>
                <div className="flex-1 min-w-0 pr-4">
                  <DialogTitle>{state.title}</DialogTitle>
                  {state.message && (
                    <DialogDescription className="mt-1">
                      {state.message}
                    </DialogDescription>
                  )}
                </div>
              </div>
            </DialogHeader>
            <DialogFooter>
              {!isAlert && state.cancelText !== '' && (
                <Button variant="secondary" onClick={handleCancel}>
                  {state.cancelText || 'Cancelar'}
                </Button>
              )}
              <Button variant={buttonVariant} onClick={handleConfirm}>
                {state.confirmText || 'Confirmar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  )
}

// Inline ConfirmDialog (controlled component) — útil cuando se prefiere render en JSX vs imperativo
export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  confirmText?: string
  cancelText?: string
  variant?: ConfirmVariant
  onConfirm: () => void | Promise<void>
  loading?: boolean
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'default',
  onConfirm,
  loading = false,
}: ConfirmDialogProps) {
  const config = variantConfig[variant]
  const buttonVariant = variant === 'danger' ? 'destructive' : 'primary'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div
              className={clsx(
                'shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
                config.iconBg
              )}
            >
              <FontAwesomeIcon
                icon={config.icon}
                className={clsx('text-base', config.iconColor)}
              />
            </div>
            <div className="flex-1 min-w-0 pr-4">
              <DialogTitle>{title}</DialogTitle>
              {description && (
                <DialogDescription className="mt-1">
                  {description}
                </DialogDescription>
              )}
            </div>
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            variant={buttonVariant}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
